"use client";

import { useEffect, useState } from 'react';
import { useSession, signIn } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useAuthModal } from '@/contexts/auth-modal-context';

// Lazy load modal since it's conditionally shown
const WelcomeBackModal = dynamic(() => import('@/components/auth/welcome-back-modal').then(mod => ({ default: mod.WelcomeBackModal })), {
  ssr: false,
  loading: () => null
});

interface HomePageClientProps {
  children: React.ReactNode;
}

export function HomePageClient({ children }: HomePageClientProps) {
  const { status, data: session } = useSession();
  const searchParams = useSearchParams();
  const { openAuthModal } = useAuthModal();
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);

  // Debug: Log session status on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Wait for session loading to complete
    if (status === 'loading') return;

    console.log('[Session Debug] Status:', status, 'Has session:', !!session, 'User:', session?.user?.email);

    // Only log warning if session looks corrupted, don't auto-logout
    if (status === 'authenticated' && session?.user) {
      const hasRequiredFields = session.user.id && session.user.email;
      if (!hasRequiredFields) {
        console.warn('[Session Debug] Session missing required fields:', {
          hasId: !!session.user.id,
          hasEmail: !!session.user.email
        });
        // Don't auto-logout - let the user continue
      }
    }
  }, [status, session]);

  // Check for auth query parameter and open modal
  useEffect(() => {
    if (!searchParams) return;
    const authParam = searchParams.get('auth');
    if (authParam === 'signin' || authParam === 'signup') {
      openAuthModal(authParam);
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('auth');
      window.history.replaceState({}, '', url.pathname);
    }
  }, [searchParams, openAuthModal]);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Welcome Back modal disabled for now
    return;

    const hasVisited = localStorage.getItem('hasVisitedUSLand');
    const dismissedThisSession = sessionStorage.getItem('welcomeBackModalDismissed');

    // Wait for auth check to complete
    if (status === 'loading') return;

    if (!hasVisited) {
      // First time visitor - just set flag
      localStorage.setItem('hasVisitedUSLand', 'true');
    } else if (status === 'unauthenticated' && !dismissedThisSession) {
      // Returning visitor, not signed in, not dismissed - show modal
      setShowWelcomeBack(true);
    }
  }, [status]);

  const handleDismiss = () => {
    sessionStorage.setItem('welcomeBackModalDismissed', 'true');
    setShowWelcomeBack(false);
  };

  const handleSignIn = () => {
    signIn('google', { callbackUrl: '/dashboard' });
  };

  return (
    <>
      {children}
      <WelcomeBackModal
        isOpen={showWelcomeBack}
        onClose={handleDismiss}
        onSignIn={handleSignIn}
      />
    </>
  );
}
